/**
 * MaritimeHealthBanner — stale/degraded banner for the SignalLedger.
 *
 * Cycle 101.1 sprint-1 Task 1.6 · beads `2sr0` / AC#6.
 *
 * Renders a single-line banner at the top of the SignalLedger when the
 * WM-relay maritime collector reports degraded health — either:
 *
 *   - `last_successful_fetch_at` > 1h ago (stale window), or
 *   - `fetch_error_rate > 0` within the 24h trailing window.
 *
 * Returns `null` when healthy, so the common-path render stays empty.
 */

import type { MaritimeAnomalyEnvelope } from '../../api/maritime';

export interface MaritimeHealthBannerProps {
  envelope: MaritimeAnomalyEnvelope | null | undefined;
  testId?: string;
}

/** Build the operator-facing copy for the degraded banner. Pure helper
 *  so unit tests can lock the tier → copy contract without rendering. */
export function maritimeBannerCopy(envelope: MaritimeAnomalyEnvelope): string {
  if (envelope.fetch_error_rate > 0) {
    const pct = Math.round(envelope.fetch_error_rate * 100);
    return `AIS feed degraded · ${pct}% fetch errors (24h)`;
  }
  if (envelope.last_successful_fetch_at === null) {
    return 'AIS feed degraded · no successful fetch yet';
  }
  return `AIS feed stale · last success ${formatRelativeTimestamp(envelope.last_successful_fetch_at)}`;
}

function formatRelativeTimestamp(iso: string): string {
  try {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return iso;
    // Match the SignalLedger timestamp style (HH:MM UTC compact).
    return dt.toISOString().replace('T', ' ').slice(0, 16) + 'Z';
  } catch {
    return iso;
  }
}

export function MaritimeHealthBanner({
  envelope,
  testId = 'maritime-health-banner',
}: MaritimeHealthBannerProps) {
  if (!envelope || !envelope.degraded) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={testId}
      style={{
        padding: '6px 14px',
        background: 'var(--cd-bg-alert, rgba(220, 140, 50, 0.12))',
        borderBottom: '1px solid var(--cd-sig-amber, #d89a3a)',
        fontFamily: 'var(--cd-font-mono)',
        fontSize: 10.5,
        letterSpacing: '0.06em',
        color: 'var(--cd-sig-amber, #d89a3a)',
      }}
    >
      {maritimeBannerCopy(envelope)}
    </div>
  );
}

export default MaritimeHealthBanner;
